import React, { useEffect, useRef, useState } from "react";
import robotIcon from "../images/roboIcon.svg";
import sendIcon from "../images/sendIconEnabled.svg";
import sendIconDisabled from "../images/sendIconDisabled.svg";
import ReactMarkdown from "react-markdown";
import maximizeIcon from "../images/maximizeMode.svg";
import minimizedIcon from "../images/minimizedMode.svg";
import cancelIcon from "../images/closeIcon.svg";
import infoIcon from "../images/yellowInfo.svg";
import "../styles/ChatBotModalStyles.css";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import copyIcon from "../images/copyIcon.png";
import { Tooltip } from "@mui/material";
import { useApi } from "../../../service/ApiContext";
import { LocalStorage } from "../../../utils/Utils";
import {
  GetChat,
  sendPrompt,
  sendPromptMessageApi,
  sendPromptSuccessApi,
} from "../data/interfaces";
import Logger from "../../../utils/Logger";
import botLogo from "../images/botLogo.svg";
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: "floating" | "popup";
}
interface Message {
  text: string;
  sender: "user" | "bot";
  code?: string;
  hasCode?: boolean;
  time: string;
  isError?: boolean;
}

const defaultPrompts = [
  { title: "Help me debug my code?" },
  { title: "What does my code do?" },
  { title: "What is JozAI?" },
];

interface MarkdownContentProps {
  content: string;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="markdown-content">
      <ReactMarkdown
        components={{
          code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const code = String(children).replace(/\n$/, "");
            const isInline = !className?.includes("language-");

            if (!isInline && match) {
              return (
                <div className="code-block-container">
                  <div className="code-block-header">
                    <span className="code-language">{match[1]}</span>
                  </div>
                  <div className="copy-block-header">
                    <button
                      className="copy-code-button"
                      onClick={() => handleCopyCode(code)}
                    >
                      <img src={copyIcon} alt="" className="copy-icon" />
                      {copiedCode === code ? "Code Copied" : "Copy Code"}
                    </button>
                  </div>

                  <pre className="code-block">
                    <code className={`language-${match[1]}`} {...props}>
                      {code}
                    </code>
                  </pre>
                </div>
              );
            } else if (isInline) {
              return (
                <code className="inline-code" {...props}>
                  {children}
                </code>
              );
            } else {
              return (
                <pre className="code-block">
                  <code {...props}>{children}</code>
                </pre>
              );
            }
          },
          pre({ children, ...props }) {
            return <>{children}</>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

// New component for error messages
interface ErrorMessageProps {
  content: string;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ content }) => {
  return (
    <div className="chatBot-error-message">
      <div className="chatBot-error-content">
        <ReactMarkdown
          components={{
            // Customize the markdown components for error styling
            p({ children, ...props }) {
              return (
                <div className="chatBot-error-paragraph" {...props}>
                  {children}
                </div>
              );
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

const AiOverlayModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  mode = "floating",
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const userData = useSelector((state: RootState) => state.profile.profileKeys);
  const [recentPrompts, setRecentPrompts] = useState<string[]>([]);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [copied, setCopied] = useState(false);
  const api = useApi();
  const [chatId, setChatId] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [isMaximized, setIsMaximized] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);

  // In AiOverlayModal component:

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Get the target element
      const target = e.target as HTMLElement;

      // Check if click is on chatbot icon or its children
      const isChatbotIcon = target.closest(".chatbot-icon");

      // Check if click is on maximize button or its children
      const isMaximizeButton = target.closest(".maximize-btn");

      if (
        modalRef.current &&
        !modalRef.current.contains(target) &&
        !isChatbotIcon &&
        !isMaximizeButton
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Function to check if content is an error JSON
  const isErrorResponse = (content: any): boolean => {
    try {
      if (typeof content === "string") {
        const parsed = JSON.parse(content);
        return parsed && typeof parsed === "object" && "error" in parsed;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Function to extract error message from JSON
  const extractErrorMessage = (content: string): string => {
    try {
      const parsed = JSON.parse(content);
      return parsed.error || "An error occurred";
    } catch {
      return "An error occurred";
    }
  };

  const getChatApi = async () => {
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const res = await api.get<GetChat>("/candidate_chat");
      if (res.data.chat_id !== null) {
        setChatId(res.data.chat_id);
      }
    } catch (err) {
      Logger.error("Error fetching chat:", err);
    } finally {
    }
  };

  // Auto-scroll only for user messages
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };
  const sendPrompt = async (chatId: string) => {
    if (!message.trim()) return;

    // Add user message to UI immediately
    setMessages((prev) => [
      ...prev,
      { text: message, sender: "user", time: getDummyTime() },
    ]);

    const currentMessage = message;
    setMessage(""); // clear input
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "40px";
      textareaRef.current.style.overflowY = "hidden";
    }
    setIsBotTyping(true);
    // Scroll to bottom when user sends message
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const res = await api.post<sendPrompt>(
        `/candidate_prompt/${chatId}`,
        { prompt: currentMessage }, // send prompt as body payload
      );

      if (res.data.message_id) {
        startPolling(res.data.message_id, chatId);
      }
    } catch (err) {
      Logger.error("Error fetching response:", err);
      setIsBotTyping(false);
    }
  };

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startPolling = (messageId: string, chatId: string) => {
    // clear any existing polling
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // start first poll
    pollOnce(messageId, chatId);
  };

  const pollOnce = async (messageId: string, chatId: string) => {
    api.setAuthToken(LocalStorage.getLocalData("access_token"));

    try {
      const response = await api.get<
        sendPromptMessageApi<sendPromptSuccessApi>
      >(`/get_candidate_message/${chatId}/${messageId}`);

      const botMessage = response.data.message;
      const status = botMessage.status;

      if (status === "Processing" || status === "Pending") {
        // 👇 Wait 2 seconds, then poll again (only AFTER previous completes)
        pollingTimeoutRef.current = setTimeout(() => {
          pollOnce(messageId, chatId);
        }, 2000);
        return;
      }

      if (status === "Done") {
        // ✅ Stop polling and show the message
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }

        if (botMessage.sender === "Model") {
          // Convert the custom date format to local time
          const convertToLocalTime = (dateString: any) => {
            // Parse the format: "2025/10/08, 05:12:40"
            const [datePart, timePart] = dateString.split(", ");
            const [year, month, day] = datePart.split("/");
            const [hours, minutes, seconds] = timePart.split(":");

            // Create a Date object in UTC (since the backend sends UTC time)
            const utcDate = new Date(
              Date.UTC(
                parseInt(year),
                parseInt(month) - 1, // months are 0-indexed
                parseInt(day),
                parseInt(hours),
                parseInt(minutes),
                parseInt(seconds),
              ),
            );

            // Convert to local time
            return utcDate.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
          };

          const localTime = convertToLocalTime(botMessage.created_at);

          // Check if the content is an error response
          const isError = isErrorResponse(botMessage.content);

          setMessages((prev) => [
            ...prev,
            {
              text: isError
                ? extractErrorMessage(botMessage.content)
                : botMessage.content,
              sender: "bot",
              time: localTime,
              isError: isError, // Mark as error for different UI treatment
            },
          ]);
        }
        setIsBotTyping(false);
      }
    } catch (err) {
      Logger.error("Polling error:", err);
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setIsBotTyping(false);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // revert after 2 seconds
  };

  // 👇 Trigger API only when modal opens
  useEffect(() => {
    if (isOpen) {
      getChatApi();
    } else {
      setMessage("");
      setMessages([]);
      setIsBotTyping(false);
      setChatId("");
      setIsMaximized(false);
    }
  }, [isOpen]); // runs every time modal open/close changes

  // 👇 Clear polling when modal closes or component unmounts
  useEffect(() => {
    if (!isOpen) {
      // Stop polling when modal closes
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    }

    // Cleanup if component unmounts
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [isOpen]);

  const getDummyTime = () => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;

    textarea.style.height = "40px"; // reset back to initial
    const newHeight = Math.min(textarea.scrollHeight, 100);
    textarea.style.height = `${newHeight}px`;

    // toggle overflow based on content
    if (textarea.scrollHeight > 100) {
      textarea.style.overflowY = "auto"; // enable scroll
    } else {
      textarea.style.overflowY = "hidden"; // hide scroll
    }

    setMessage(e.target.value);
  };
  const handlePromptClick = (promptText: string) => {
    setMessage(promptText);

    // Adjust textarea height immediately
    if (textareaRef.current) {
      textareaRef.current.style.height = "40px"; // reset height
      const newHeight = Math.min(textareaRef.current.scrollHeight, 100);
      textareaRef.current.style.height = `${newHeight}px`;
      textareaRef.current.style.overflowY =
        textareaRef.current.scrollHeight > 100 ? "auto" : "hidden";
    }
  };

  if (!isOpen) return null;

  return (
    <div className={mode === "popup" ? "ChatBotModalPopupOverlay" : "ChatBotModalOverlay"}>
      <div
        ref={modalRef}
        className={`ChatBotModelContent ${isMaximized ? "maximized" : ""}`}
      >
        {/* Maximize button */}
        <div className="bot-header">
          <img src={botLogo} alt="JozAI" className="bot-logo" />

          <div className="maximize-btn">
            <img
              src={isMaximized ? minimizedIcon : maximizeIcon}
              alt="maximize"
              style={{
                cursor: "pointer",
                height: "30px",
                width: "30px",
              }}
              onClick={toggleMaximize}
            />

            <img
              src={cancelIcon}
              alt="close"
              style={{
                cursor: "pointer",
                height: "30px",
                width: "30px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        </div>
        {/* Robot stays always visible */}

        {/* <img src={robotIcon} alt="" className="robotIcon" /> */}

        {/* If no messages yet -> show intro + prompts */}
        {messages.length === 0 ? (
          <>
            <div>
              <div className="chatBotText">
                Hi {userData.first_name} 👋
                <div> I am JozAI, your personal learning assistant.</div>
              </div>
              <br />
              <div className="chatBotText">
                I am here to help you with programming questions, and general
                queries
              </div>
            </div>
            <div className="chatBotPrompts">
              <p
                style={{
                  fontFamily: "Mazzard",
                  fontSize: "12px",
                  color: "#6789A9",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                Recommended prompts
              </p>

              {(() => {
                // Remove duplicates (avoid showing the same prompt twice)
                const filteredDefaults = defaultPrompts
                  .map((p) => p.title)
                  .filter((d) => !recentPrompts.includes(d));

                // Always show recent first, then fill with defaults → max 5 total
                const combined = [...recentPrompts, ...filteredDefaults].slice(
                  0,
                  5,
                );

                return combined.map((prompt, index) => {
                  const isLong = prompt.length > 25;
                  const displayText = isLong
                    ? prompt.slice(0, 25) + "..."
                    : prompt;

                  return isLong ? (
                    <Tooltip key={index} title={prompt} arrow>
                      <p
                        className="suggestPrompts"
                        style={{ margin: "4px 0", cursor: "pointer" }}
                        onClick={() => handlePromptClick(prompt)}
                      >
                        {displayText}
                      </p>
                    </Tooltip>
                  ) : (
                    <p
                      key={index}
                      className="suggestPrompts"
                      style={{ margin: "4px 0", cursor: "pointer" }}
                      onClick={() => handlePromptClick(prompt)}
                    >
                      {displayText}
                    </p>
                  );
                });
              })()}
            </div>
          </>
        ) : (
          <div className="chatMessages" ref={chatContainerRef}>
            {messages.map((msg, idx) => (
              <div key={idx} className="messageWrapper">
                {msg.sender === "bot" && (
                  <div
                    className={`botMessage ${
                      msg.isError ? "chatBot-error-message-container" : ""
                    }`}
                  >
                    <div
                      className={`chatBubble bot ${
                        msg.isError ? "chatBot-error-bubble" : ""
                      }`}
                    >
                      {msg.isError ? (
                        <ErrorMessage content={msg.text} />
                      ) : (
                        <MarkdownContent content={msg.text} />
                      )}
                    </div>
                    <div className="chatTime botTime">{msg.time}</div>
                  </div>
                )}

                {/* User Message */}
                {msg.sender === "user" && (
                  <div className="userMessage">
                    <div className="chatBubble user">
                      <span>{msg.text}</span>
                    </div>
                    <div className="chatTime userTime">{msg.time}</div>
                  </div>
                )}
              </div>
            ))}
            {/* Show shimmer while bot is generating response */}
            {isBotTyping && (
              <div
                className={
                  isMaximized === false ? "shimmer-loader" : "maxShimmerLoader"
                }
              >
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
                <div
                  className={
                    isMaximized === false ? "shimmerLine" : "maxShimmerLine"
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Input bar */}
        <div className="chatBotMessageTab">
          {/* Input row with bar and icon */}
          <div className="chatBotInputRow">
            <textarea
              ref={textareaRef}
              className="chatMessageBar"
              placeholder="How can I assist today?"
              value={message}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (chatId) sendPrompt(chatId);
                }
              }}
              disabled={isBotTyping}
            />

            <div className="sendIconContainer">
              {message === "" || chatId === "" ? (
                <img
                  src={sendIconDisabled}
                  alt=""
                  style={{ height: "40px", width: "40px" }}
                />
              ) : (
                <img
                  src={sendIcon}
                  alt=""
                  style={{
                    height: "40px",
                    width: "40px",
                    cursor: "pointer",
                  }}
                  // onClick={handleSend}
                  onClick={() => {
                    if (chatId) sendPrompt(chatId);
                  }}
                />
              )}
            </div>
          </div>

          {/* Info row */}
          <div className="chatBotInfoRow">
            <img src={infoIcon} alt="" className="chatBotInfoIcon" />
            <span
              className={
                isMaximized === false
                  ? "chatBotInfoText"
                  : "chatBotInfoMaximizedText"
              }
            >
              AI can make mistakes. Please refer to trusted sources for accurate
              fact-based answers.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiOverlayModal;
